import React from 'react';
import ProfileImage from './ProfileImage';
import { COLORS, LAYOUT } from '../../constants';

// Edit Icon
const EditIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

// Mail Icon
const MailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

// Calendar Icon
const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const ProfileCard = ({ userData, onEditProfile }) => {
  const cardStyles = {
    container: {
      background: '#fff',
      borderRadius: 16,
      padding: LAYOUT.spacing.xl,
      boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
      marginBottom: LAYOUT.spacing.lg
    },
    
    content: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: LAYOUT.spacing.lg
    },
    
    imageWrapper: {
      position: 'relative'
    },
    
    verifiedBadge: {
      position: 'absolute',
      bottom: 4,
      right: 4,
      width: 32,
      height: 32,
      borderRadius: '50%',
      background: COLORS.primary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '3px solid #fff',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
    },
    
    checkIcon: {
      width: 16,
      height: 16,
      stroke: '#fff',
      strokeWidth: 3,
      fill: 'none'
    },
    
    infoSection: {
      textAlign: 'center',
      width: '100%'
    },
    
    name: {
      fontSize: 24,
      fontWeight: 700,
      color: COLORS.primary,
      marginBottom: LAYOUT.spacing.xs
    },
    
    bio: {
      fontSize: 15,
      color: COLORS.text.secondary,
      lineHeight: 1.5,
      marginTop: LAYOUT.spacing.xs,
      marginBottom: LAYOUT.spacing.sm,
      fontStyle: 'italic'
    },
    
    detailsContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: LAYOUT.spacing.sm,
      marginTop: LAYOUT.spacing.md,
      alignItems: 'center'
    },
    
    detailRow: {
      display: 'flex',
      alignItems: 'center',
      gap: LAYOUT.spacing.xs,
      color: COLORS.text.secondary,
      fontSize: 14
    },
    
    iconWrapper: {
      color: COLORS.primary,
      display: 'flex',
      alignItems: 'center'
    },
    
    editButton: {
      width: '100%',
      padding: '14px 24px',
      background: `linear-gradient(135deg, ${COLORS.primary} 0%, #9d0d1d 100%)`,
      color: '#fff',
      border: 'none',
      borderRadius: 12,
      fontSize: 16,
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: LAYOUT.spacing.sm,
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 12px rgba(206, 17, 38, 0.2)',
      marginTop: LAYOUT.spacing.md
    }
  };

  return (
    <div style={cardStyles.container}>
      <div style={cardStyles.content}>
        <div style={cardStyles.imageWrapper}>
          <ProfileImage 
            src={userData.profilePic}
            alt="profile"
          />
          {userData.emailVerified && (
            <div style={cardStyles.verifiedBadge}>
              <svg style={cardStyles.checkIcon} viewBox="0 0 24 24">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
          )}
        </div>
        
        <div style={cardStyles.infoSection}>
          <div style={cardStyles.name}>{userData.name}</div>
          
          {userData.bio && (
            <div style={cardStyles.bio}>{userData.bio}</div>
          )}
          
          <div style={cardStyles.detailsContainer}>
            <div style={cardStyles.detailRow}>
              <div style={cardStyles.iconWrapper}>
                <MailIcon />
              </div>
              <span>{userData.email}</span>
            </div>
            
            {userData.joinedAt?.toDate && (
              <div style={cardStyles.detailRow}>
                <div style={cardStyles.iconWrapper}>
                  <CalendarIcon />
                </div>
                <span>
                  Joined {userData.joinedAt.toDate().toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
              </div>
            )}
          </div>
        </div>
        
        <button
          style={cardStyles.editButton}
          onClick={onEditProfile}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(206, 17, 38, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(206, 17, 38, 0.2)';
          }}
        >
          <EditIcon />
          Edit Profile
        </button>
      </div>
    </div>
  );
};

export default ProfileCard;